import cls from "@fvilers/cls";
import { ComponentPropsWithoutRef, useRef } from "react";
import { useFieldContext } from "../../hooks/useAppForm";
import { useIdOr } from "../../hooks/useIdOr";
import { FieldError, FieldErrors } from "../FieldErrors/FieldErrors";
import { showSelectPicker } from "./helpers/showSelectPicker";
import styles from "./Select.module.css";

export type SelectProps = {
  errors?: FieldError;
  label: string;
  readOnly?: boolean;
} & ComponentPropsWithoutRef<"select">;

export function Select({
  className,
  errors,
  label,
  readOnly,
  style,
  ...props
}: SelectProps) {
  const selectRef = useRef<HTMLSelectElement>(null);
  const hasErrors = Boolean(
    errors && (Array.isArray(errors) ? errors.length : true),
  );
  const id = useIdOr(props.id);
  const errorId = hasErrors ? `${id}-error` : undefined;

  const handleLabelClick = (e: React.MouseEvent<HTMLLabelElement>) => {
    const select = selectRef.current;

    if (!select) return;

    if (select.contains(e.target as Node)) {
      return;
    }

    e.preventDefault();
    select.focus();
    showSelectPicker(select);
  };

  return (
    <FieldErrors data-slot="control" errorId={errorId} errors={errors}>
      <label
        aria-label={label}
        className={cls(styles.SelectLabel, className)}
        onClick={handleLabelClick}
        style={style}
      >
        <span>{label}</span>
        <select
          aria-describedby={errorId ?? undefined}
          aria-invalid={!!hasErrors}
          aria-readonly={readOnly ? true : undefined}
          className={styles.Select}
          data-autofocus={props.autoFocus ? true : undefined}
          ref={selectRef}
          {...props}
          id={id}
        />
      </label>
    </FieldErrors>
  );
}

export function SelectField(props: SelectProps) {
  const field = useFieldContext<string>();
  return (
    <Select
      errors={field.state.meta.errors.map((e) =>
        typeof e === "string" ? e : e?.message,
      )}
      {...props}
      form={field.form.formId}
      onBlur={field.handleBlur}
      onChange={(e) => {
        field.handleChange(e.target.value);
        props.onChange?.(e);
      }}
      value={field.state.value}
    />
  );
}
